import {
  ensureDesignTokenMaps,
  type Node,
  type NodeFrame,
  type OverlayChild,
  type PageBlueprint,
} from '@menuvera/engine';

import type { TemplateDocument } from '../nodeTree';

/** v3: overlay children keep frames; flow children stay in flex/grid. */
export const SPATIAL_VERSION = 3;

function overlayChildToFrame(child: OverlayChild): NodeFrame {
  return {
    x: child.x,
    y: child.y,
    width: child.width,
    height: child.height,
    rotate: child.rotate,
    z: child.z,
    pinX: child.pinX,
    pinY: child.pinY,
  };
}

function defaultHeightForKind(kind: Node['kind']): number {
  if (kind === 'grid' || kind === 'repeater') return 0.35;
  if (kind === 'spacer') return 0.04;
  return 0.08;
}

/** Frames stamped by v2 migrate (`x: 8%, w: 84%, h: 8%` on a 0.05 + n·0.10 grid). */
export function isAutoStampedFrame(frame: NodeFrame, kind: Node['kind']): boolean {
  const height = defaultHeightForKind(kind);
  if (Math.abs(frame.x - 0.08) > 0.001) return false;
  if (Math.abs(frame.width - 0.84) > 0.001) return false;
  if (Math.abs(frame.height - height) > 0.001) return false;
  if ((frame.rotate ?? 0) !== 0) return false;
  const gap = 0.02;
  for (let index = 0; index < 24; index += 1) {
    const y = Math.min(0.9 - height, 0.05 + index * (height + gap));
    if (Math.abs(frame.y - y) < 0.001) return true;
  }
  return false;
}

function stripAutoFrame(node: Node): Node {
  const next: Node =
    node.frame && isAutoStampedFrame(node.frame, node.kind)
      ? (({ frame: _removed, ...rest }) => rest as Node)(node)
      : node;

  switch (next.kind) {
    case 'stack':
    case 'grid':
    case 'group':
    case 'frame':
      return { ...next, children: next.children.map(stripAutoFrame) };
    case 'repeater':
      return {
        ...next,
        itemTemplate: stripAutoFrame(next.itemTemplate),
        ...(next.empty ? { empty: stripAutoFrame(next.empty) } : {}),
      };
    case 'slot':
      return next.defaultChild
        ? { ...next, defaultChild: stripAutoFrame(next.defaultChild) }
        : next;
    case 'overlay':
      return {
        ...next,
        children: next.children.map((child) => ({
          ...child,
          node: stripAutoFrame(child.node),
        })),
      };
    default:
      return next;
  }
}

function migrateNode(node: Node): Node {
  switch (node.kind) {
    case 'stack':
    case 'grid':
    case 'group':
    case 'frame':
      return migrateContainerRoot(node);
    case 'repeater':
      return {
        ...node,
        itemTemplate: migrateNode(node.itemTemplate),
        ...(node.empty ? { empty: migrateNode(node.empty) } : {}),
      };
    case 'slot':
      return node.defaultChild
        ? { ...node, defaultChild: migrateNode(node.defaultChild) }
        : node;
    default:
      return node;
  }
}

function migrateContainerRoot(
  root: Extract<Node, { kind: 'stack' | 'grid' | 'group' | 'frame' }>,
): Node {
  const overlayIndex = root.children.findIndex((child) => child.kind === 'overlay');
  const overlay = overlayIndex >= 0 ? root.children[overlayIndex] : null;

  const hoisted: Node[] = [];
  if (overlay?.kind === 'overlay') {
    for (const child of overlay.children) {
      const migrated = migrateNode(child.node);
      const frame = migrated.frame ?? overlayChildToFrame(child);
      hoisted.push({ ...migrated, frame });
    }
  }

  const flowChildren = root.children
    .filter((child) => child.kind !== 'overlay')
    .map((child) => migrateNode(child));

  const children =
    overlayIndex >= 0
      ? [...flowChildren.slice(0, overlayIndex), ...hoisted, ...flowChildren.slice(overlayIndex)]
      : flowChildren;

  return stripAutoFrame({ ...root, children });
}

function migrateBlueprints(blueprints: PageBlueprint[]): PageBlueprint[] {
  return blueprints.map((blueprint) => ({
    ...blueprint,
    root: migrateNode(blueprint.root),
  }));
}

/** Deep-clone via JSON — safe for frozen GraphQL payloads. */
function cloneDocument(document: TemplateDocument): TemplateDocument {
  return JSON.parse(JSON.stringify(document)) as TemplateDocument;
}

const NULLABLE_LENGTH_KEYS = [
  'gap',
  'columnGap',
  'rowGap',
  'rowHeight',
  'size',
  'thickness',
] as const;

/** Drop JSON-null length fields so the layout engine never calls `.slice` on null. */
function scrubNullLengths(node: Node): Node {
  const next = { ...node } as Node & Record<string, unknown>;
  for (const key of NULLABLE_LENGTH_KEYS) {
    if (next[key] === null) delete next[key];
  }
  if (next.layout && typeof next.layout === 'object') {
    const layout = { ...(next.layout as Record<string, unknown>) };
    for (const key of Object.keys(layout)) {
      if (layout[key] === null) delete layout[key];
    }
    next.layout = layout as Node['layout'];
  }
  if (next.style && typeof next.style === 'object') {
    const style = { ...(next.style as Record<string, unknown>) };
    for (const key of ['padding', 'margin', 'radius'] as const) {
      const value = style[key];
      if (value === null) delete style[key];
      else if (value && typeof value === 'object' && !Array.isArray(value)) {
        const insets = { ...(value as Record<string, unknown>) };
        for (const side of Object.keys(insets)) {
          if (insets[side] === null) delete insets[side];
        }
        style[key] = insets;
      }
    }
    next.style = style as Node['style'];
  }

  switch (next.kind) {
    case 'stack':
    case 'grid':
    case 'group':
    case 'frame':
      return { ...next, children: next.children.map(scrubNullLengths) };
    case 'repeater':
      return {
        ...next,
        itemTemplate: scrubNullLengths(next.itemTemplate),
        ...(next.empty ? { empty: scrubNullLengths(next.empty) } : {}),
      };
    case 'slot':
      return next.defaultChild
        ? { ...next, defaultChild: scrubNullLengths(next.defaultChild) }
        : next;
    case 'overlay':
      return {
        ...next,
        children: next.children.map((child) => ({
          ...child,
          node: scrubNullLengths(child.node),
        })),
      };
    default:
      return next;
  }
}

function scrubDocumentLengths(document: TemplateDocument): TemplateDocument {
  return {
    ...document,
    blueprints: (document.blueprints ?? []).map((blueprint) => ({
      ...blueprint,
      root: scrubNullLengths(blueprint.root),
    })),
    pages: document.pages?.map((page) => ({
      ...page,
      root: scrubNullLengths(page.root),
    })),
    variants: document.variants?.map((variant) => ({
      ...variant,
      blueprints: variant.blueprints.map((blueprint) => ({
        ...blueprint,
        root: scrubNullLengths(blueprint.root),
      })),
    })),
  };
}

/** Hoists legacy overlays; strips v2 auto-stamped frames. Idempotent. */
export function migrateToFrames(document: TemplateDocument): TemplateDocument {
  const cloned = cloneDocument(document);
  // Repair maps stripped by Mongoose minimize (e.g. shadows: {} → missing).
  cloned.tokens = ensureDesignTokenMaps(cloned.tokens);

  const spatiallyReady =
    (cloned.spatialVersion ?? 0) >= SPATIAL_VERSION
      ? cloned
      : {
          ...cloned,
          spatialVersion: SPATIAL_VERSION,
          blueprints: migrateBlueprints(cloned.blueprints ?? []),
          pages: cloned.pages?.map((page) => ({
            ...page,
            root: migrateNode(page.root),
          })),
          variants: cloned.variants?.map((variant) => ({
            ...variant,
            blueprints: migrateBlueprints(variant.blueprints),
          })),
        };

  return scrubDocumentLengths(spatiallyReady);
}
